import { Autocomplete, Card, Grid, Icon, IconButton, InputLabel, Tooltip, Switch, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable } from "utils/Functions";
import { get, post, postNoResponse } from "utils/WebService";
import TipoOcorrenciaCreateEdit from "./TipoOcorrenciaCreateEdit";
import MDAuditModal from "components/MDAuditModal";
import { categoriaOcorrenciaEnum } from "utils/Enums";
import { useUser } from "context/UserContext";

export default function TipoOcorrenciaConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();

    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [auditTitle, setAuditTitle] = useState("");
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAudit, setLoadingAudit] = useState(false);

    const handleOpenAuditoriaRow = (row) => {
        const id = row.id || row.cvId;
        setAuditTitle(`Auditoria: ${row.nome || ("ID " + id)}`);
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get(`/tipo-ocorrencia/${id}/historico`)
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria do tipo de ocorrência:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const handleOpenAuditoriaGeral = () => {
        setAuditTitle("Histórico de Auditoria - Tipos de Ocorrência");
        setAuditModalOpen(true);
        setLoadingAudit(true);
        get("/tipo-ocorrencia/historico-geral")
            .then(res => setAuditLogs(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error("Erro ao carregar auditoria geral de ocorrências:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };
    const isAdminOrSuporte = userPerfils && userPerfils.some(p => {
        const pName = (p?.nome || (typeof p === 'string' ? p : "")).toUpperCase();
        return p?.tipoPerfil === 4 || p?.tipoPerfil === 6 || pName === 'ADMINISTRADOR' || pName === 'SUPORTE';
    });

    const isLiberadoEquipeFilter = userPerfils && userPerfils.some(p => {
        const pName = (p?.nome || p?.ccNome || (typeof p === 'string' ? p : "")).toUpperCase();
        const pCode = p?.tipoPerfil ?? p?.cvCodPerfil ?? p?.cvTipo;
        return (
            pCode === 1 || pCode === 4 || pCode === 6 ||
            pName.includes("ADMIN") || pName.includes("SUPORTE") || pName.includes("GESTOR") || pName.includes("GERENTE")
        );
    });

    const [equipesList, setEquipesList] = useState([]);

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        nome: '',
        ativo: '',
        categoria: '',
        equipeId: ''
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    useEffect(() => {
        get("/equipe/list-all").then((data) => {
            setEquipesList(data?.list || data?.data || (Array.isArray(data) ? data : []));
        }).catch(() => setEquipesList([]));
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };
        tableState.current = updatedTableState;

        post("/tipo-ocorrencia/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
        }).then(({ data: resultList, pages, recordsTotal }) => {
            let list = resultList || [];
            if (filters.equipeId) {
                list = list.filter(item => {
                    if (!item.equipesIds || item.equipesIds.length === 0) return true;
                    return item.equipesIds.includes(String(filters.equipeId));
                });
            }
            setPages(pages);
            setData(list);
            setRecordsTotal(list.length);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false);
            setAplicar(false);
        });
    }, [filters.equipeId]);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
    };

    const handleToggleAtivo = (row, newStatus) => {
        const rowId = row.id !== undefined ? row.id : row.cvId;
        const payload = {
            ...row,
            id: rowId,
            ativo: newStatus
        };
        postNoResponse("/tipo-ocorrencia/save", payload)
            .then(() => {
                setSbMessage(`Tipo de Ocorrência "${row.nome}" ${newStatus ? "ativado" : "inativado"} com sucesso!`);
                setSuccessSB(true);
                fetchData({ ...tableState });
            })
            .catch((err) => {
                const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Erro ao alterar status.";
                setSbMessage(errorMsg);
                setErrorSB(true);
            });
    };

    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!itemToDelete) return;
        const rowId = itemToDelete.id !== undefined ? itemToDelete.id : itemToDelete.cvId;
        postNoResponse(`/tipo-ocorrencia/delete/${rowId}`)
            .then(() => {
                setSbMessage(`Tipo de Ocorrência "${itemToDelete.nome}" excluído com sucesso!`);
                setSuccessSB(true);
                fetchData({ ...tableState });
            })
            .catch((err) => {
                const errorMsg = err?.response?.data?.message || err?.data?.message || err?.message || "Não é possível excluir o Tipo de Ocorrência pois ele está associado a registros no sistema.";
                setSbMessage(errorMsg);
                setErrorSB(true);
            })
            .finally(() => {
                setDeleteDialogOpen(false);
                setItemToDelete(null);
            });
    };

    const userColumns = [
        {
            header: "nome",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "categoria",
            accessorKey: "categoria",
            cell: info => {
                const val = info.getValue();
                const cat = categoriaOcorrenciaEnum.find(x => x.code === val);
                return <DefaultCell>{cat ? cat.label : "Ocorrência"}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "Situação da Ficha",
            accessorKey: "situacaoFichaLabel",
            cell: info => {
                const label = info.getValue() || info.row.original.situacaoFichaLabel;
                if (label) return <DefaultCell>{label}</DefaultCell>;

                const code = info.row.original.situacaoFicha;
                if (code !== null && code !== undefined) {
                    const situacaoOpt = [
                        { code: 1, label: "Devolvido" },
                        { code: 2, label: "Cobrança" },
                        { code: 3, label: "Negociação" },
                        { code: 4, label: "Pesquisa" },
                        { code: 5, label: "Suspenso" },
                        { code: 6, label: "Migrado" },
                        { code: 7, label: "Quitado" }
                    ].find(s => Number(s.code) === Number(code));
                    if (situacaoOpt) return <DefaultCell>{situacaoOpt.label}</DefaultCell>;
                }

                return <DefaultCell>{"-"}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "ativo",
            accessorKey: "ativo",
            cell: info => {
                const row = info.row.original;
                const isChecked = !!info.getValue();
                return (
                    <MDBox display="flex" alignItems="center">
                        <Switch
                            checked={isChecked}
                            onChange={(e) => handleToggleAtivo(row, e.target.checked)}
                            color="info"
                        />
                    </MDBox>
                );
            },
            isSorted: false
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => {
                const row = info.row.original;
                return (
                    <MDBox display="flex" alignItems="center">
                        <Tooltip title="Histórico de Auditoria">
                            <IconButton onClick={() => handleOpenAuditoriaRow(row)}>
                                <MDTypography variant="h4">
                                    <Icon color="info">history</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                            <IconButton
                                onClick={() => {
                                    const rowId = row.id || row.cvId;
                                    if (rowId) {
                                        setSelectedId(rowId);
                                        setIsCreateEdit(true);
                                    } else {
                                        console.error("ID não encontrado no objeto:", row);
                                        alert("Erro: O ID da ocorrência não foi retornado pelo servidor.");
                                    }
                                }}>
                                <MDTypography variant="h4">
                                    <Icon color="inherit">edit</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                            <IconButton onClick={() => handleDeleteClick(row)}>
                                <MDTypography variant="h4">
                                    <Icon color="error">delete</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    </MDBox>
                );
            },
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        let filtered = [];
        Object.keys(filters).forEach(filterKey => {
            if (filters[filterKey] !== '' && filters[filterKey] !== null && filters[filterKey] !== undefined) {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        });
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
    };

    const handleCleanFilter = () => {
        setFilters({ nome: '', ativo: '', categoria: '', equipeId: '' });
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [] });
    };

    const equipeOptions = [
        { id: '', nome: 'Todas as Equipes' },
        ...equipesList.map(eq => ({ id: String(eq.id || eq.cvId), nome: eq.nome || eq.ccNome }))
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <TipoOcorrenciaCreateEdit id={selectedId} closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#4B4F5C" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item xs={12} sm={isLiberadoEquipeFilter ? (isAdminOrSuporte ? 2.5 : 3) : (isAdminOrSuporte ? 3 : 4)}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Nome</InputLabel>
                            <MDInput size="small" placeholder="Nome..." value={filters.nome} onChange={({ target }) => setFilters({ ...filters, nome: target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={isLiberadoEquipeFilter ? (isAdminOrSuporte ? 2 : 2.5) : (isAdminOrSuporte ? 2 : 4)}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Status</InputLabel>
                            <Autocomplete
                                options={[{ label: 'Todos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }]}
                                value={[{ label: 'Todos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }].find(opt => opt.value === filters.ativo) || { label: 'Todos', value: '' }}
                                onChange={(event, newValue) => {
                                    setFilters({ ...filters, ativo: newValue ? newValue.value : '' });
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                renderInput={(params) => <MDInput {...params} size="small" placeholder="Status..." />}
                                disableClearable
                            />
                        </Grid>
                        {isAdminOrSuporte && (
                            <Grid item xs={12} sm={isLiberadoEquipeFilter ? 2.5 : 3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Categoria</InputLabel>
                                <Autocomplete
                                    options={categoriaOcorrenciaEnum}
                                    value={categoriaOcorrenciaEnum.find(opt => opt.code === filters.categoria) || null}
                                    onChange={(event, newValue) => {
                                        setFilters({ ...filters, categoria: newValue ? newValue.code : '' });
                                    }}
                                    getOptionLabel={opt => opt.label}
                                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                                    renderInput={(params) => <MDInput {...params} size="small" placeholder="Categoria..." />}
                                />
                            </Grid>
                        )}
                        {isLiberadoEquipeFilter && (
                            <Grid item xs={12} sm={2.5}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Equipe</InputLabel>
                                <Autocomplete
                                    options={equipeOptions}
                                    value={equipeOptions.find(opt => opt.id === filters.equipeId) || equipeOptions[0]}
                                    onChange={(event, newValue) => {
                                        setFilters({ ...filters, equipeId: newValue ? newValue.id : '' });
                                    }}
                                    getOptionLabel={(opt) => opt.nome || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    renderInput={(params) => <MDInput {...params} size="small" placeholder="Equipe..." />}
                                    disableClearable
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={isLiberadoEquipeFilter ? 2.5 : (isAdminOrSuporte ? 4 : 4)} display="flex" alignItems="center" justifyContent="flex-end" pt={1}>
                            <MDBox display="flex" gap={1} mt={3}>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="info" 
                                    onClick={() => { setSelectedId(null); setIsCreateEdit(true); }}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important"
                                    }}
                                >
                                    Adicionar
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="outlined" 
                                    color="info" 
                                    onClick={handleOpenAuditoriaGeral}
                                >
                                    <Icon sx={{ mr: 0.5 }}>history</Icon> Auditoria Geral
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={handleCleanFilter}
                                    sx={{
                                        color: darkMode ? "#ffffff !important" : undefined,
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                                    }}
                                >
                                    Limpar
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="info" 
                                    onClick={handleApplyFilter}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important"
                                    }}
                                >
                                    Aplicar
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable loading={loadingTable} columnData={userColumns} data={data} fetchData={fetchData} totalPages={pages} totalRecords={recordsTotal} changeFilter={aplicar} tableState={tableState} />
            </Grid>

            {/* Modal de Confirmação de Exclusão */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#424550" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        Tem certeza que deseja excluir o Tipo de Ocorrência <strong>{itemToDelete?.nome}</strong>?
                    </MDTypography>
                    <br/>
                    <MDTypography variant="caption" color="error">
                        Atenção: Se esta ocorrência estiver associada a devedores ou históricos no sistema, a exclusão será bloqueada.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton 
                        onClick={() => setDeleteDialogOpen(false)} 
                        variant="outlined" 
                        color="secondary"
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="contained">
                        Excluir
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Modal de Auditoria */}
            <MDAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                title={auditTitle}
                historicos={auditLogs}
                loading={loadingAudit}
                entityName="Tipo de Ocorrência"
            />

            {/* Feedback / Notificações */}
            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Atenção"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />
        </Grid>
    );
}
